// Athlete-side track & field marks-ledger editor. Rendered by GameStatsPage for
// track athletes in place of the football position/year stat table (which has
// no fields for track events). Add/edit/delete marks against /api/event-marks;
// the event picklist is catalog-driven, and lower_is_better / mark_numeric are
// computed client-side per event kind (see lib/eventMarks.js).
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useCatalog } from '../../context/CatalogContext';
import Button from '../Button';
import ConfirmModal from '../ConfirmModal';
import VerifiedFlame from '../VerifiedFlame';
import {
  TIMING_OPTIONS, VENUE_OPTIONS, TIMING_LABEL, VENUE_LABEL,
  lowerIsBetterFor, showTimingFor, showWindFor, parseMarkNumeric,
} from '../../lib/eventMarks';
import { resolveGetConfig } from './editorConfig';
import './AthleteEditor.css';

const currentYear = () => new Date().getFullYear();

const EMPTY_MARK = {
  event_key: '', mark_value: '', meet_name: '', meet_date: '',
  season_year: '', venue_type: '', timing_type: '', wind_reading: '', wind_legal: true,
};

export default function TrackMarksEditor({ getConfig: getConfigProp }) {
  const { API_URL } = useAuth();
  const catalog = useCatalog();
  const [marks, setMarks] = useState([]);
  const [form, setForm] = useState(EMPTY_MARK);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);

  const groupedPositions = catalog.groupedPositionsFor('track');
  const positions = catalog.positionsFor('track');
  const groupForEvent = useMemo(() => {
    const m = new Map(positions.map(p => [p.slug, p.group]));
    return (key) => m.get(key) || '';
  }, [positions]);
  const labelForEvent = useMemo(() => {
    const m = new Map(positions.map(p => [p.slug, p.label]));
    return (key) => m.get(key) || key;
  }, [positions]);

  const getConfig = resolveGetConfig(getConfigProp);

  useEffect(() => { fetchMarks(); }, []);

  const fetchMarks = async () => {
    try {
      const res = await axios.get(`${API_URL}/event-marks/me`, getConfig());
      setMarks(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching marks:', err);
    }
  };

  const showMsg = (text, type = '') => {
    setMessage(text); setMessageType(type);
    setTimeout(() => setMessage(''), type === 'error' ? 4000 : 3000);
  };

  const setField = (patch) => setForm(prev => ({ ...prev, ...patch }));

  const group = groupForEvent(form.event_key);
  const wantsTiming = form.event_key && showTimingFor(group);
  const wantsWind = form.event_key && showWindFor(form.event_key, group);

  const buildPayload = () => {
    const g = groupForEvent(form.event_key);
    const seasonYear = form.season_year
      ? Number(form.season_year)
      : (form.meet_date ? Number(form.meet_date.slice(0, 4)) : null);
    return {
      event_key: form.event_key,
      mark_value: form.mark_value.trim(),
      mark_numeric: parseMarkNumeric(form.mark_value, g),
      lower_is_better: lowerIsBetterFor(g),
      season_year: Number.isFinite(seasonYear) ? seasonYear : null,
      meet_name: form.meet_name.trim() || null,
      meet_date: form.meet_date || null,
      venue_type: form.venue_type || null,
      timing_type: showTimingFor(g) ? (form.timing_type || null) : null,
      wind_reading: showWindFor(form.event_key, g) && form.wind_reading !== ''
        ? Number(form.wind_reading) : null,
      wind_legal: showWindFor(form.event_key, g) && form.wind_reading !== ''
        ? !!form.wind_legal : null,
    };
  };

  const doSave = async () => {
    const payload = buildPayload();
    try {
      if (editingId) {
        await axios.patch(`${API_URL}/event-marks/me/${editingId}`, payload, getConfig());
      } else {
        await axios.post(`${API_URL}/event-marks/me`, payload, getConfig());
      }
      resetForm();
      fetchMarks();
      showMsg(editingId ? 'Mark updated' : 'Mark added');
    } catch (err) {
      showMsg('Error saving mark', 'error');
    }
  };

  const saveMark = () => {
    if (!form.event_key) { showMsg('Pick an event', 'error'); return; }
    if (!form.mark_value.trim()) { showMsg('Enter a mark', 'error'); return; }
    // Editing a verified mark clears its verification on the backend — confirm.
    const editingVerified = editingId && marks.find(m => m.id === editingId)?.verified;
    if (editingVerified) {
      setConfirmAction({
        title: 'Unverify Mark',
        message: 'This mark is currently verified. Editing it will clear the verification, and it can only be re-verified by ProZpects staff or a Partner.',
        confirmLabel: 'Continue', danger: true,
        onConfirm: () => { setConfirmAction(null); doSave(); },
        onCancel: () => setConfirmAction(null),
      });
      return;
    }
    doSave();
  };

  const resetForm = () => { setForm(EMPTY_MARK); setEditingId(null); };

  const startEdit = (m) => {
    setEditingId(m.id);
    setForm({
      event_key: m.event_key,
      mark_value: m.mark_value || '',
      meet_name: m.meet_name || '',
      meet_date: m.meet_date || '',
      season_year: m.season_year || '',
      venue_type: m.venue_type || '',
      timing_type: m.timing_type || '',
      wind_reading: m.wind_reading != null ? String(m.wind_reading) : '',
      wind_legal: m.wind_legal !== false,
    });
  };

  const deleteMark = async (id) => {
    try {
      await axios.delete(`${API_URL}/event-marks/me/${id}`, getConfig());
      if (editingId === id) resetForm();
      fetchMarks();
      showMsg('Mark removed');
    } catch (err) {
      console.error('Error deleting mark:', err);
    }
  };

  // Group marks by event, ordered to match catalog event order.
  const grouped = useMemo(() => {
    const order = new Map(positions.map((p, i) => [p.slug, i]));
    const byEvent = new Map();
    for (const m of marks) {
      if (!byEvent.has(m.event_key)) byEvent.set(m.event_key, []);
      byEvent.get(m.event_key).push(m);
    }
    return [...byEvent.entries()]
      .sort((a, b) => (order.get(a[0]) ?? 999) - (order.get(b[0]) ?? 999))
      .map(([key, rows]) => ({ key, label: labelForEvent(key), rows }));
  }, [marks, positions, labelForEvent]);

  const yearOptions = Array.from({ length: 8 }, (_, i) => currentYear() - i);

  return (
    <>
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`} role="alert">
          {message}
        </div>
      )}

      <div className="settings-section">
        <h2 className="settings-section__title">Event Marks</h2>
        <p className="settings-section__desc">
          Log a performance for each event — one row per meet. Your best-ever mark (PR) and
          best-in-season mark (SB) are flagged automatically.
        </p>

        <div className="form-row">
          <div className="form-field">
            <label className="form-field__label">Event</label>
            <select
              className="form-field__select"
              value={form.event_key}
              onChange={(e) => setField({ event_key: e.target.value })}
              data-testid="track_mark_event"
            >
              <option value="">Select event…</option>
              {groupedPositions.map(g => (
                <optgroup key={g.group} label={g.group}>
                  {g.positions.map(p => (
                    <option key={p.slug} value={p.slug}>{p.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label className="form-field__label">Mark</label>
            <input
              type="text"
              className="form-field__input"
              value={form.mark_value}
              onChange={(e) => setField({ mark_value: e.target.value })}
              placeholder="e.g., 10.52 or 4:05.10"
              data-testid="track_mark_value"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="form-field__label">Meet</label>
            <input
              type="text"
              className="form-field__input"
              value={form.meet_name}
              onChange={(e) => setField({ meet_name: e.target.value })}
              placeholder="e.g., State Championships"
            />
          </div>
          <div className="form-field" style={{ maxWidth: 170 }}>
            <label className="form-field__label">Date</label>
            <input
              type="date"
              className="form-field__input"
              value={form.meet_date}
              onChange={(e) => setField({ meet_date: e.target.value })}
            />
          </div>
          <div className="form-field" style={{ maxWidth: 120 }}>
            <label className="form-field__label">Season</label>
            <select
              className="form-field__select"
              value={form.season_year}
              onChange={(e) => setField({ season_year: e.target.value })}
            >
              <option value="">Year…</option>
              {yearOptions.map(yr => <option key={yr} value={yr}>{yr}</option>)}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-field" style={{ maxWidth: 160 }}>
            <label className="form-field__label">Venue</label>
            <select
              className="form-field__select"
              value={form.venue_type}
              onChange={(e) => setField({ venue_type: e.target.value })}
            >
              {VENUE_OPTIONS.map(o => <option key={o.value || 'none'} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {wantsTiming && (
            <div className="form-field" style={{ maxWidth: 200 }}>
              <label className="form-field__label">Timing</label>
              <select
                className="form-field__select"
                value={form.timing_type}
                onChange={(e) => setField({ timing_type: e.target.value })}
              >
                {TIMING_OPTIONS.map(o => <option key={o.value || 'none'} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )}
          {wantsWind && (
            <>
              <div className="form-field" style={{ maxWidth: 130 }}>
                <label className="form-field__label">Wind (m/s)</label>
                <input
                  type="number"
                  step="0.1"
                  className="form-field__input"
                  value={form.wind_reading}
                  onChange={(e) => setField({ wind_reading: e.target.value })}
                  placeholder="e.g., 1.2"
                />
              </div>
              <div className="form-field" style={{ maxWidth: 150, justifyContent: 'flex-end' }}>
                <label className="form-field__label" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 22 }}>
                  <input
                    type="checkbox"
                    checked={form.wind_legal}
                    onChange={(e) => setField({ wind_legal: e.target.checked })}
                  />
                  Wind legal (≤2.0)
                </label>
              </div>
            </>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <Button type="button" variant="accent" size="sm" onClick={saveMark} data-testid="track_mark_save">
            {editingId ? 'Save Changes' : 'Add Mark'}
          </Button>
          {editingId && (
            <Button type="button" variant="ghost" size="sm" onClick={resetForm}>Cancel</Button>
          )}
        </div>

        {marks.length === 0 && (
          <p style={{ color: '#94a3b8', fontSize: '13px', marginTop: 12 }}>No marks logged yet.</p>
        )}
        {grouped.map(g => (
          <div key={g.key} className="ae-mark-group" style={{ marginTop: 14 }}>
            <div className="ae-mark-group__head">{g.label}</div>
            {g.rows.map(m => (
              <div key={m.id} className="accolade-row">
                <div className="accolade-row__body">
                  <span className="accolade-row__title">
                    {m.mark_value}
                    {m.is_pr && <span className="ae-mark-badge ae-mark-badge--pr">PR</span>}
                    {m.is_sb && <span className="ae-mark-badge ae-mark-badge--sb">SB</span>}
                    {m.verified && <VerifiedFlame />}
                  </span>
                  <span className="accolade-row__meta">
                    {m.meet_name && <span className="accolade-row__src">{m.meet_name}</span>}
                    {m.meet_date && <span className="accolade-row__src">{m.meet_date}</span>}
                    {m.wind_reading != null && (
                      <span className="accolade-row__src">
                        {(Number(m.wind_reading) > 0 ? '+' : '') + Number(m.wind_reading).toFixed(1)} m/s
                        {m.wind_legal === false ? ' (illegal)' : ''}
                      </span>
                    )}
                    {m.timing_type && <span className="accolade-chip">{TIMING_LABEL[m.timing_type] || m.timing_type}</span>}
                    {m.venue_type && <span className="accolade-chip">{VENUE_LABEL[m.venue_type] || m.venue_type}</span>}
                  </span>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => startEdit(m)}>Edit</Button>
                <Button type="button" variant="danger" size="sm" onClick={() => deleteMark(m.id)}>Delete</Button>
              </div>
            ))}
          </div>
        ))}
      </div>

      {confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel={confirmAction.confirmLabel}
          danger={confirmAction.danger}
          onConfirm={confirmAction.onConfirm}
          onCancel={confirmAction.onCancel || (() => setConfirmAction(null))}
        />
      )}
    </>
  );
}
